// ── ADMIN: HELP ──────────────────────────────────────────────────────
// The staff guides Lori works from, moved into the portal so they sit next to
// the screens they describe instead of living as bookmarked links.
//
// Two views: an index of cards grouped by kind (a-help), and a reader
// (a-help-doc). Both are driven entirely by the manifest the server sends
// from src/Routes/HelpRoutes.php — adding a guide is one entry there plus one
// fragment file, and nothing in this file changes. The guide bodies are
// unstyled HTML fragments; portal.html's .help-doc block styles them.
//
// The fragments are trusted, server-owned files (not user input), so they're
// injected as HTML — that's the whole point of them. Everything drawn from
// the manifest still goes through esc().
import { apiFetch } from '../core/api.js';
import { currentHelpSlug, setCurrentHelpSlug } from '../core/state.js';
import { fmtD } from '../core/format.js';
import { toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route, refresh } from '../core/routing.js';

// Manifest, fetched once per session. null until the first load resolves —
// VIEWS renders synchronously, so the index paints a placeholder and the
// VIEW_INIT hook below fills it in.
let HELP_DOCS = null;
// Guide bodies, keyed by slug, cached so re-opening one is instant.
const HELP_BODIES = {};

async function loadManifest() {
  if (HELP_DOCS) return HELP_DOCS;
  const res = await apiFetch('/help');
  HELP_DOCS = res.docs;
  return HELP_DOCS;
}

export function openHelpDoc(slug) {
  setCurrentHelpSlug(slug);
  route('a-help-doc');
}

// Contextual entry point for a "?" button anywhere else in the admin UI:
// openHelpDoc('workshops', 'catalog') lands on that guide at that section.
export function openHelpSection(slug, sectionId) {
  // Set before route(), which runs the reader's VIEW_INIT synchronously and
  // will consume this as soon as the guide's HTML is in the DOM.
  pendingAnchor = sectionId;
  openHelpDoc(slug);
}
let pendingAnchor = null;

// ── Index ───────────────────────────────────────────────────────────

function renderHelpCard(doc) {
  return `
      <button class="help-card" data-action="help-open" data-slug="${esc(doc.slug)}">
        <h3>${esc(doc.title)}</h3>
        <p>${esc(doc.blurb)}</p>
        ${doc.status ? `<div class="help-card-status">${esc(doc.status)}</div>` : ''}
        <div class="help-card-meta">
          <span>Updated ${esc(fmtD(doc.updated))}</span>
          <span>·</span>
          <span>${doc.sections.length} section${doc.sections.length === 1 ? '' : 's'}</span>
        </div>
      </button>`;
}

function renderHelpIndex() {
  if (!HELP_DOCS) return `<div class="empty">Loading guides…</div>`;
  if (!HELP_DOCS.length) return `<div class="empty">No guides available.</div>`;

  // Groups render in the order the manifest lists them, so the server decides
  // what comes first without this file carrying its own list of group names.
  const groups = [];
  HELP_DOCS.forEach(d => {
    const g = groups.find(x => x.label === d.group);
    if (g) g.docs.push(d);
    else groups.push({ label: d.group, docs: [d] });
  });

  return `<div class="help-groups">${groups.map(g => `
    <div>
      <div class="help-group-label">${esc(g.label)}</div>
      <div class="help-cards">${g.docs.map(renderHelpCard).join('')}</div>
    </div>`).join('')}</div>`;
}

VIEWS['a-help'] = () => `
  <div class="page-head">
    <h2 class="serif">Help &amp; <em>Guides</em></h2>
    <p class="sub">Step-by-step walkthroughs for the things that only come around once or twice a year.</p>
  </div>
  <div id="helpIndex">${renderHelpIndex()}</div>`;

VIEW_INIT['a-help'] = () => {
  wireHelpIndex();
  if (HELP_DOCS) return;
  loadManifest()
    .then(() => refresh())
    .catch(e => { HELP_DOCS = null; toastError(e.message); });
};

function wireHelpIndex() {
  document.querySelectorAll('[data-action="help-open"]').forEach(btn => {
    btn.addEventListener('click', () => openHelpDoc(btn.dataset.slug));
  });
}

// ── Reader ──────────────────────────────────────────────────────────

VIEWS['a-help-doc'] = () => {
  const doc = (HELP_DOCS || []).find(d => d.slug === currentHelpSlug);
  const cached = HELP_BODIES[currentHelpSlug];
  return `
    <div class="help-doc-bar">
      <button class="help-back" data-action="help-back">← All guides</button>
      ${doc && doc.goTo ? `<button class="help-goto" data-action="help-goto" data-view="${esc(doc.goTo)}">Go to ${esc(doc.goToLabel)}</button>` : ''}
    </div>
    <div class="page-head">
      <h2 class="serif" id="helpDocTitle">${doc ? esc(doc.title) : 'Loading…'}</h2>
    </div>
    <div class="help-layout">
      <div id="helpDocBody" class="help-doc">${cached || '<div class="empty">Loading…</div>'}</div>
      <div class="help-toc" id="helpDocToc">${doc ? renderToc(doc) : ''}</div>
    </div>`;
};

function renderToc(doc) {
  return `
    <div class="help-group-label">On this page</div>
    ${doc.sections.map(s => `<button data-action="help-jump" data-section="${esc(s.id)}">${esc(s.label)}</button>`).join('')}`;
}

VIEW_INIT['a-help-doc'] = () => {
  document.querySelectorAll('[data-action="help-back"]').forEach(btn => {
    btn.addEventListener('click', () => route('a-help'));
  });
  wireDocChrome();

  const slug = currentHelpSlug;
  if (!slug) { route('a-help'); return; }
  // The manifest may not be loaded at all on a deep link straight into a
  // guide (portal.html#a-help-doc/workshops), in which case the title and the
  // table of contents are still blank — load both, then re-render.
  const needsManifest = !HELP_DOCS;
  if (HELP_BODIES[slug] && !needsManifest) { scrollToPending(); return; }

  Promise.all([loadManifest(), apiFetch('/help/' + encodeURIComponent(slug))])
    .then(([, doc]) => {
      HELP_BODIES[slug] = doc.html;
      // Guard against the guide resolving after the admin has already
      // navigated somewhere else.
      if (currentHelpSlug !== slug) return;
      refresh();
      scrollToPending();
    })
    .catch(e => {
      toastError(e.message);
      // The title placeholder would otherwise sit on "Loading…" forever. A
      // guide the manifest doesn't list (renamed, or one this admin's tier
      // can't see) has no title to fall back on.
      const title = document.getElementById('helpDocTitle');
      const known = (HELP_DOCS || []).find(d => d.slug === slug);
      if (title) title.textContent = known ? known.title : 'Guide unavailable';
      const body = document.getElementById('helpDocBody');
      if (body) body.innerHTML = `<div class="empty">That guide could not be loaded.</div>`;
    });
};

function wireDocChrome() {
  document.querySelectorAll('[data-action="help-goto"]').forEach(btn => {
    btn.addEventListener('click', () => route(btn.dataset.view));
  });
  // Buttons rather than anchors: an href="#section" would overwrite the hash,
  // which is where the portal keeps its current view id.
  document.querySelectorAll('[data-action="help-jump"]').forEach(b => {
    b.addEventListener('click', () => scrollToSection(b.dataset.section));
  });
}

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function scrollToPending() {
  if (!pendingAnchor) return;
  const id = pendingAnchor;
  pendingAnchor = null;
  scrollToSection(id);
}
